import { z } from "zod"

import { meRetentionSchema } from "./account.ts"
import { invoiceUsageSchema } from "./invoices.ts"
import { meOrganisationEntrySchema } from "./teams.ts"

/**
 * Organisation summary (ADR-015: me caches user, organisation, entitlements; sync cursors
 * and Storage paths are organisation-scoped). `null` until organisations ship (phase 3);
 * the product is single-organisation per user.
 */
export const meOrganisationSchema = z.object({
  id: z.uuid(),
  name: z.string(),
})

export type MeOrganisation = z.infer<typeof meOrganisationSchema>

/**
 * AI receipt-read usage. `monthlyQuota` null = unlimited; `resetsOn` is the first day of the
 * next Australia/Sydney month (`YYYY-MM-DD`).
 */
export const aiReadsSchema = z.object({
  monthlyQuota: z.number().int().nonnegative().nullable(),
  used: z.number().int().nonnegative(),
  resetsOn: z.string().date(),
})

/**
 * Present (non-null) while the account is scheduled for deletion (T322). Lenient on purpose:
 * dates are plain strings so an old client never fails to decode `/me`.
 */
export const meDeletionSchema = z.object({
  requestedAt: z.string(),
  purgeAfter: z.string(),
})

export type MeDeletion = z.infer<typeof meDeletionSchema>

/**
 * Language-assistant turns used in the current month (T341). `monthlyQuota` null = unlimited.
 * Lenient: older clients ignore it, older servers omit it.
 */
export const assistantTurnsSchema = z.object({
  used: z.number().int(),
  monthlyQuota: z.number().int().nullable(),
})

export type AssistantTurns = z.infer<typeof assistantTurnsSchema>

export type AiReads = z.infer<typeof aiReadsSchema>

/**
 * Response schema for GET /api/v1/me (system-design §5.2, ADR-015).
 *
 * - `email`: the identity the auth provider (Supabase) has already verified. The server
 *   returns it as-is; clients must not re-validate the format, since zod's strict email
 *   regex rejects addresses Supabase accepts (e.g. `a&b@example.com`, some IDN domains).
 * - `plan`: always "free" until billing integration (realised in phase 3); "logbook" is
 *   already part of the subscription design (system-design §4) and accepted from day one.
 * - `organisation`: the caller's organisation; the server creates a "Personal" one on first call,
 *   so a signed-in user gets an object (the schema stays nullable for older servers and fixtures).
 * - `entitlements.monthlyTripQuota`: trips per month with full visibility, `null` = unlimited.
 *   Free tier (PRD R6.11, ADR-015): trips 1–20 full (quota 20, PRD R6.10), trips 21–30 uploaded but masked
 *   (visibility = masked), from trip 31 the server returns QUOTA_EXCEEDED and the phone
 *   holds the trip locally (hold_reason = quota).
 */
export const meResponseSchema = z.object({
  id: z.uuid(),
  email: z.string().min(1),
  plan: z.enum(["free", "logbook", "pro"]),
  organisation: meOrganisationSchema.nullable(),
  /**
   * Every organisation the user belongs to (ADR-017 2.2). Absent on servers that predate Teams;
   * a client then falls back to `organisation` alone. The top-level `organisation` and
   * `entitlements` always describe the personal organisation, so older clients never see a team.
   */
  organisations: z.array(meOrganisationEntrySchema).optional(),
  /**
   * Rollback switch for in-app purchase (T1173): true only while the server has
   * `STORE_BILLING_ENABLED` on. Missing or false means option A, so a client shows no purchase UI.
   * Lenient and additive: old servers omit it, old clients ignore it.
   */
  storePurchasing: z.boolean().optional(),
  /** Absent on servers that predate account deletion; null when the account is active. */
  deletion: meDeletionSchema.nullable().optional(),
  /**
   * Data-retention clock of the personal organisation (T1084, T822): the plan-lapse date and the
   * soft-delete and purge dates. Null while active; absent on servers that predate it. Additive:
   * ship the server only after clients that ignore unknown keys are released.
   */
  retention: meRetentionSchema.nullable().optional(),
  entitlements: z.object({
    monthlyTripQuota: z.number().int().nonnegative().nullable(),
    /**
     * Feature ids the plan includes (receipts plan 1.1). Lenient `string[]`: clients ignore
     * unknown ids. Absent on servers that predate the receipts feature.
     */
    features: z.array(z.string()).optional(),
    /** Features the organisation already holds data for on the server (read-only if not in `features`). */
    dataPresent: z.array(z.string()).optional(),
    /** AI receipt reads in the current Australia/Sydney month. */
    aiReads: aiReadsSchema.optional(),
    /** Language-assistant usage in the current month; absent on servers without the assistant. */
    assistantTurns: assistantTurnsSchema.optional(),
    /** Invoices created in the current Sydney month (T412); absent on servers without invoicing. */
    invoices: invoiceUsageSchema.optional(),
    /**
     * True only when the server exempts this account from quota masking (T942, for test accounts).
     * Set by an operator in the database, never by the client; absent on older servers, which
     * clients read as false. A client must not offer any local override of the mask.
     */
    quotaMaskExempt: z.boolean().optional(),
    /**
     * Active (non-archived) vehicles the organisation may hold: the plan base plus purchased extra
     * vehicles (T1343). The server computes it with the same function that enforces the limit.
     * Lenient and additive: absent on older servers, in which case a client keeps its built-in
     * table; no upper bound, so a future plan never breaks parsing.
     */
    vehicleQuota: z.number().int().nonnegative().optional(),
  }),
})

export type MeResponse = z.infer<typeof meResponseSchema>
